import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  ADVERTISER_FILTER_THRESHOLD,
  ADVERTISER_TYPE_LABELS,
  AMC_MARKETPLACES,
  AMC_MARKETPLACE_ID,
  AdvertiserType,
  AdvertiserTypeType
} from '../amc-instances-constants';
import { AdvertiserInput, AdvertiserUpdate, AmcInstance } from '../amc-instances-types';
import { filterAdvertisers, modeLabel, writeSuccess } from '../amc-instances-utils';
import {
  useAdvertiserUpdates,
  useInstanceAdvertisers,
  useModifyAdvertisers
} from '../utils/useAmcInstances';

// Edit-only: Amazon requires the instance to exist before advertisers can be attached.

interface AdvertiserRow {
  type: AdvertiserTypeType;
  id: string;
  marketplaceId: string;
}

const BLANK_ROW: AdvertiserRow = {
  type: AdvertiserType.DISPLAY,
  id: '',
  marketplaceId: ''
};

const TYPE_OPTIONS = [
  { value: AdvertiserType.DISPLAY, label: ADVERTISER_TYPE_LABELS[AdvertiserType.DISPLAY] },
  {
    value: AdvertiserType.SPONSORED_ADS,
    label: ADVERTISER_TYPE_LABELS[AdvertiserType.SPONSORED_ADS]
  }
];

interface AdvertiserManagerProps {
  instance: AmcInstance;
  live: boolean;
}

export const AdvertiserManager = ({ instance, live }: AdvertiserManagerProps) => {
  const { instanceId } = instance;
  const attached = useInstanceAdvertisers(instanceId);
  const updates = useAdvertiserUpdates(instanceId);
  const modifyAdvertisers = useModifyAdvertisers();
  const setError = useSetError();

  const [rows, setRows] = useState<AdvertiserRow[]>([{ ...BLANK_ROW }]);

  const pending = (updates.data ?? []).filter(update => update.pending);

  const setRow = (index: number, patch: Partial<AdvertiserRow>) =>
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const addRow = () => setRows(prev => [...prev, { ...BLANK_ROW }]);

  const removeRow = (index: number) =>
    setRows(prev => (prev.length === 1 ? [{ ...BLANK_ROW }] : prev.filter((_, i) => i !== index)));

  const changeType = (index: number, type: AdvertiserTypeType) =>
    setRow(index, {
      type,
      // Sponsored Ads requires a marketplace; DSP doesn't use one.
      marketplaceId:
        type === AdvertiserType.SPONSORED_ADS ? rows[index].marketplaceId || AMC_MARKETPLACE_ID : ''
    });

  const submit = async () => {
    const advertisersToAdd: AdvertiserInput[] = rows
      .filter(row => row.id.trim())
      .map(row => ({
        id: row.id.trim(),
        type: row.type,
        ...(row.type === AdvertiserType.SPONSORED_ADS
          ? { marketplaceId: (row.marketplaceId || AMC_MARKETPLACE_ID).trim() }
          : {})
      }));

    if (advertisersToAdd.length === 0) {
      setError({ message: 'Enter at least one advertiser id to add.' });
      return;
    }

    try {
      const result = await modifyAdvertisers.mutateAsync({
        instanceId,
        advertisersToAdd,
        dryRun: !live
      });
      if (live) {
        Mixpanel.track(MxE.RecordCreated, {
          page: PAGE_NAMES.AMC_INSTANCES,
          entity: 'amc_advertiser'
        });
      }
      const count = conditionallyPluralizeWithCount(advertisersToAdd.length, 'advertiser');
      setError(
        writeSuccess(
          live,
          `Add ${count} to “${instance.instanceName}”`,
          `Amazon queued it as update ${result.updateId ?? '—'}; it won't appear above until ` +
            'the update completes.'
        )
      );
      if (live) {
        setRows([{ ...BLANK_ROW }]);
      }
    } catch (e) {
      setError({
        message: `Couldn't add advertisers: ${e.message}`,
        reportError: e
      });
    }
  };

  return (
    <div className='amc-advertisers'>
      <hr className='amc-divider' />

      <AttachedAdvertisers attached={attached} />
      <PendingUpdates updates={pending} isLoading={updates.isLoading} />

      <div className='amc-subtitle'>Add advertisers</div>
      <div className='amc-help'>
        Add one or many. DSP uses a numeric advertiser id; Sponsored Ads uses an ENTITY id and
        requires a marketplace — pick the advertiser’s own, not the account’s. One submit can cover
        only one marketplace.
      </div>

      {rows.map((row, index) => (
        <div className='amc-advertiser-row' key={index}>
          <Dropdown
            options={TYPE_OPTIONS}
            value={row.type}
            onChange={value => changeType(index, value ?? AdvertiserType.DISPLAY)}
          />
          <Form.Control
            data-testid='amc-advertiser-id-input'
            placeholder={row.type === AdvertiserType.DISPLAY ? 'DSP advertiser id' : 'ENTITY… id'}
            value={row.id}
            onChange={e => setRow(index, { id: e.target.value })}
          />
          {row.type === AdvertiserType.SPONSORED_ADS && (
            <Dropdown
              options={AMC_MARKETPLACES}
              placeholder='Marketplace…'
              searchable
              creatable
              value={row.marketplaceId || null}
              onChange={value => setRow(index, { marketplaceId: value ?? '' })}
              reactSelectProps={{ formatCreateLabel: input => `Use marketplace "${input}"` }}
            />
          )}
          <IconButton
            size={Size.SM}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            aria-label='Remove advertiser'
            onClick={() => removeRow(index)}
          >
            <MdClose />
          </IconButton>
        </div>
      ))}

      <div className='amc-advertiser-actions'>
        <Button
          size={Size.SM}
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={addRow}
        >
          Add another
        </Button>
        <Button size={Size.SM} loading={modifyAdvertisers.isPending} onClick={submit}>
          {modeLabel('Add advertisers', live)}
        </Button>
      </div>
    </div>
  );
};

interface AttachedAdvertisersProps {
  attached: ReturnType<typeof useInstanceAdvertisers>;
}

const AttachedAdvertisers = ({ attached }: AttachedAdvertisersProps) => {
  const [query, setQuery] = useState('');
  const advertisers = attached.data ?? [];
  const filtered = filterAdvertisers(advertisers, query);

  if (attached.isLoading) return <Spinner size={20} />;

  if (attached.error) {
    return (
      <div className='amc-banner error'>Couldn’t load advertisers: {attached.error.message}</div>
    );
  }

  if (advertisers.length === 0) {
    return (
      <>
        <div className='amc-subtitle'>Advertisers on this instance</div>
        <div className='amc-help'>No advertisers are currently tied to this instance.</div>
      </>
    );
  }

  return (
    <>
      <div className='amc-subtitle'>
        Advertisers on this instance
        <span className='amc-count-chip'>{advertisers.length}</span>
      </div>
      {advertisers.length > ADVERTISER_FILTER_THRESHOLD && (
        <Form.Control
          className='amc-advertiser-search'
          type='text'
          placeholder='Filter advertisers by name, id, type…'
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      )}
      <ul className='amc-advertiser-list'>
        {filtered.map(advertiser => (
          <li key={advertiser.id}>
            <span className='amc-advertiser-name'>{advertiser.name ?? '—'}</span>
            <span className='amc-mono'>{advertiser.id}</span>
            <span className='amc-tag'>{advertiser.type}</span>
            {advertiser.marketplaceId && (
              <span className='amc-tag'>{advertiser.marketplaceId}</span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
};

interface PendingUpdatesProps {
  updates: AdvertiserUpdate[];
  isLoading: boolean;
}

const PendingUpdates = ({ updates, isLoading }: PendingUpdatesProps) => {
  if (isLoading) return <Spinner size={20} />;
  if (updates.length === 0) return undefined;

  return (
    <>
      <div className='amc-subtitle'>
        Pending updates
        <span className='amc-count-chip'>{updates.length}</span>
      </div>
      <div className='amc-help'>
        Submitted to Amazon but not applied yet. These advertisers won’t appear in the list above
        until their update completes — don’t resubmit them.
      </div>
      <ul className='amc-pending-list'>
        {updates.map(update => (
          <li key={String(update.updateId)} data-testid='amc-pending-update'>
            <div className='amc-pending-head'>
              <span className='amc-mono'>{update.toAdd.map(a => a.id).join(', ') || '—'}</span>
              {update.toAdd.map(advertiser => (
                <span className='amc-tag' key={`${advertiser.id}-${advertiser.type}`}>
                  {advertiser.type}
                </span>
              ))}
              <span className='amc-status updating'>{update.status ?? '—'}</span>
              <span className='amc-update-id'>update {update.updateId ?? '—'}</span>
            </div>
            {update.pendingVerificationReason && (
              <div className='amc-pending-reason'>{update.pendingVerificationReason}</div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
};
